import React, { useEffect, useMemo } from 'react';
import {
    Box,
    Grid,
    Stack,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    CircularProgress,
} from '@mui/material';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import type { PCAReportField } from 'c-sdk';
import { ElasticSearchHistogramInterval, EsPCAReportField } from 'c-sdk';

import DspSplitReportPage from 'c-components/HiddenReport/Components/DspSplitReportPage';
import SplitChartWithInsights from 'c-components/HiddenReport/Components/SplitChartWithInsights';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from 'recharts';
import uniledLogo from 'public/uniledLogo.png';
import poweredByUniledLogo from 'public/poweredByUniledLogo.png';
import { dspReportTarget } from './dspReportTarget';

type Environment = 'local' | 'production' | 'staging';
type ReportTemplate = 'Linear_report' | 'DSP_report';

interface Payload {
    campaign_id: number;
    jwt: string;
    powerpoint_template: ReportTemplate;
    skin_id: number;
    environment: Environment;
    email: string;
    language: 'GB' | 'US';
    start_date?: string;
    end_date?: string;
    third_color?: string;
}

type MultRow = {
    display_owner: string;
    display_format: string;
    sum_of_play: number;
    dsp_impressions: number;
    geopath_impressions?: number;
    variance: number;
    dsp_avg_impressions?: number;
    geopath_avg_impressions?: number;
    average_im?: number; // non-geopath “Average IM”
    im_variance?: number; // non-geopath “IM VAR”
    variance_percent: number; // decimal (e.g. 0.87)
};

const num = (n: any) => Number(n ?? 0) || 0;
const fmtInt = (n: number) => Math.round(n).toLocaleString();
const fmt2 = (n: number) => num(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
const formatPercentValue = (percent: number) => {
    if (percent > 0 && percent < 0.5) {
        const rounded = percent.toFixed(2);
        return rounded === '0.00' ? '0%' : `${rounded}%`;
    }
    return `${Math.round(percent)}%`;
};
const fmtPct = (d: number) => formatPercentValue((d ?? 0) * 100).replace('%', ' %');

/** pick top 5 by dsp_impressions where %VAR sign matches */
function pickTopBottom(rows: MultRow[], positive: boolean, count = 5) {
    const filtered = rows.filter(r => (positive ? r.variance_percent > 0 : r.variance_percent < 0));
    return filtered.sort((a, b) => b.dsp_impressions - a.dsp_impressions).slice(0, count);
}

/** accept array or object, return MultRow[] with safe defaults */
function normalizeRows(input: any): MultRow[] {
    const raw = input?.data?.data ?? input?.data ?? input ?? [];
    if (!Array.isArray(raw)) return [];
    return raw.map((r: any) => ({
        display_owner: String(r.display_owner ?? r.owner ?? ''),
        display_format: String(r.display_format ?? r.format ?? ''),
        sum_of_play: num(r.sum_of_play ?? r.plays),
        dsp_impressions: num(r.dsp_impressions),
        geopath_impressions: num(r.geopath_impressions),
        variance: num(r.variance ?? r.im_variance),
        dsp_avg_impressions: num(r.dsp_avg_impressions),
        geopath_avg_impressions: num(r.geopath_avg_impressions),
        average_im: num(r.average_im),
        im_variance: num(r.im_variance),
        variance_percent: Number(r.variance_percent ?? 0),
    }));
}

/** helpers (same behaviour as Linear) */
function formatDate(dateString: string, locale: string): string {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const m = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const d = date.getUTCDate().toString().padStart(2, '0');
    const y = date.getUTCFullYear();
    return locale === 'GB' ? `${d}/${m}/${y}` : `${m}/${d}/${y}`;
}
function reformatDateForFilteredDates(dateStr: string, locale: string): string {
    const ddmmyyyy = /^\d{2}-\d{2}-\d{4}$/;
    if (ddmmyyyy.test(dateStr)) {
        const [dd, mm, yyyy] = dateStr.split('-');
        return locale === 'GB' ? `${dd}/${mm}/${yyyy}` : `${mm}/${dd}/${yyyy}`;
    }
    const [yyyy, mm, dd] = dateStr.split('-');
    if (!yyyy || !mm || !dd) return '';
    return locale === 'GB' ? `${dd}/${mm}/${yyyy}` : `${mm}/${dd}/${yyyy}`;
}
const nf = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });
const fmtN = (v?: number) =>
    typeof v === 'number' && Number.isFinite(v) ? nf.format(Math.round(v)) : '0';

const DOW_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const dowIndex = (label: string) => {
    const s = String(label).toLowerCase();
    const i = DOW_ORDER.findIndex(d => d.toLowerCase().startsWith(s));
    return i === -1 ? 99 : i;
};

// Map ES numeric buckets to names (handles 1..7 and 0..6)
const dowLabelFromKey = (k: any): string => {
    const s = String(k);
    if (/^\d+$/.test(s)) {
        const n = Number(s);
        if (n >= 1 && n <= 7) return DOW_ORDER[n - 1]; // 1=Mon … 7=Sun
        if (n >= 0 && n <= 6) {
            const map0 = [
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday',
            ];

            return map0[n];
        }
    }
    return s;
};

const DSPReport: React.FC<{ payload: Payload }> = ({ payload }) => {
    const { start, data: campaign } = useAPIClientRequest(ApiClient.Entities.Campaign.get);
    const { start: skinStart, data: skin } = useAPIClientRequest(ApiClient.Entities.Skin.get);
    const { start: pcaSummaryStart, data: pcaSummary } = useAPIClientRequest(
        ApiClient.Reports.pcaReport,
    );
    const { start: histogramStart, data: histogram } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.histogram,
    );
    const { start: formatStart, data: format } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.pcaGroupByField,
    );
    const { start: dowStart, data: dow } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.pcaGroupByField,
    );
    const { start: hourStart, data: hour } = useAPIClientRequest(
        ApiClient.ReportsElasticSearch.pcaGroupByField,
    );
    const { start: geoStart, data: geo } = useAPIClientRequest(
        ApiClient.Reports.geopathImpressionMultipliers,
    );
    const { start: impStart, data: imp } = useAPIClientRequest(
        ApiClient.Reports.impressionMultipliers,
    );
    const isReady = useMemo(
        () =>
            !!campaign &&
            !!skin &&
            !!pcaSummary &&
            !!histogram &&
            !!format &&
            !!dow &&
            !!hour &&
            !!geo &&
            !!imp,
        [campaign, dow, format, geo, histogram, hour, imp, pcaSummary, skin],
    );
    useEffect(() => {
        if (isReady) {
            // eslint-disable-next-line no-underscore-dangle
            window.__REACT_READY__ = true;
        }
    }, [isReady]);
    const campaignType = useMemo(() => campaign?.data?.data?.type, [campaign]);
    const routeTranslation = useMemo(
        () => campaignType === 'vistar_uk' || campaignType === 'hivestack_uk',
        [campaignType],
    );
    useEffect(() => {
        if (!payload?.campaign_id || !payload?.skin_id) return;

        start(payload.campaign_id, [
            'client',
            'date',
            'market',
            'market.country',
            'creativeThumbnails',
        ]);
        skinStart(payload.skin_id);
        pcaSummaryStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            timeFrame: 'summary' as any,
            fieldFilters: { 'filters.scheduled': ['IN', 'OUT', 'UNBOOKED'] } as unknown as Partial<
                Record<PCAReportField, any[]>
            >,
        });
        histogramStart({
            campaignId: payload.campaign_id,
            field: EsPCAReportField.StartDate,
            interval: ElasticSearchHistogramInterval.Day,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: { 'filters.scheduled': ['IN', 'OUT', 'UNBOOKED'] } as unknown as Partial<
                Record<PCAReportField, any[]>
            >,
        });
        formatStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: { 'filters.scheduled': ['IN', 'OUT', 'UNBOOKED'] } as unknown as Partial<
                Record<PCAReportField, any[]>
            >,
            field: EsPCAReportField.DisplayFormat,
        });
        dowStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: { 'filters.scheduled': ['IN', 'OUT', 'UNBOOKED'] } as unknown as Partial<
                Record<PCAReportField, any[]>
            >,
            field: EsPCAReportField.Dow,
        });
        hourStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: { 'filters.scheduled': ['IN', 'OUT', 'UNBOOKED'] } as unknown as Partial<
                Record<PCAReportField, any[]>
            >,
            field: EsPCAReportField.Hour,
        });
        geoStart({
            id: payload.campaign_id,
        });
        impStart({
            id: payload.campaign_id,
        });
    }, [
        payload,
        start,
        skinStart,
        pcaSummaryStart,
        histogramStart,
        formatStart,
        dowStart,
        hourStart,
        geoStart,
        impStart,
    ]);

    const brand = (skin as any)?.data?.data?.primary_color || (skin as any)?.data?.primary_color; // fallback magenta

    const logoUrl = useMemo(() => (skin as any)?.data?.data?.logo_path ?? '', [skin]);

    const c = (campaign as any)?.data?.data;
    const title: string = c?.name || 'DSP Campaign';
    const liveDates =
        c?.date_start && c?.date_end
            ? `${formatDate(c.date_start, payload.language)} – ${formatDate(
                  c?.date_end,
                  payload.language,
              )}`
            : '';

    const reportDates =
        payload.start_date && payload.end_date
            ? `${reformatDateForFilteredDates(
                  payload.start_date,
                  payload.language,
              )} – ${reformatDateForFilteredDates(payload.end_date, payload.language)}`
            : (() => {
                  const daily = (pcaSummary as any)?.data?.data?.daily_stats;
                  if (daily && typeof daily === 'object') {
                      const keys = Object.keys(daily).sort();
                      if (keys.length) {
                          return `${reformatDateForFilteredDates(
                              keys[0],
                              payload.language,
                          )} – ${reformatDateForFilteredDates(
                              keys[keys.length - 1],
                              payload.language,
                          )}`;
                      }
                  }
                  return '';
              })();

    const stats = (pcaSummary as any)?.data?.data?.campaign_stats ?? {};
    const impacts = stats?.impacts ?? 0;
    const plays = stats?.sum_of_plays as number | undefined;
    const seconds = stats?.sum_of_time as number | undefined;
    const fmtTime = (sec?: number) => {
        if (!sec && sec !== 0) return '0m';
        const totalM = Math.round(sec / 60);
        const h = Math.floor(totalM / 60);
        const m = totalM % 60;
        return h > 0 ? `${h.toLocaleString()}hrs ${m}mins` : `${m}mins`;
    };

    const lineData = useMemo(() => {
        const raw = (histogram as any)?.data?.data ?? (histogram as any)?.data ?? {};
        const keys = Object.keys(raw);
        if (!keys.length) return [] as Array<{ label: string; value: number }>;

        keys.sort();
        let cum = 0;
        return keys.map(k => {
            const val = Number((raw?.[k]?.[k]?.impacts ?? raw?.[k]?.impacts ?? 0) as number) || 0;
            cum += val;
            return {
                label: formatDate(k, payload.language),
                value: cum,
            };
        });
    }, [histogram, payload.language]);

    const adjustText = '';
    const ownersEntries = useMemo(
        () =>
            Object.entries((pcaSummary as any)?.data?.data?.display_owner ?? {}).filter(
                ([k]) => (k ?? '').trim() !== '',
            ),
        [pcaSummary],
    );

    type OwnerRow = { label: string; impacts: number };
    const ownersData: OwnerRow[] = useMemo(
        () =>
            ownersEntries
                .map(([label, v]: any) => ({
                    label,
                    impacts: Number(v?.impacts ?? 0),
                }))
                .sort((a, b) => b.impacts - a.impacts)
                .splice(0, 10),
        [ownersEntries],
    );

    const totalOwnerImpacts = useMemo(
        () => ownersData.reduce((s, d) => s + d.impacts, 0),
        [ownersData],
    );
    const metricWord = payload.language === 'GB' ? 'Impacts' : 'Impressions';
    const metricWordSignular = payload.language === 'GB' ? 'Impact' : 'Impression';

    const TOP_N = 6;

    const pct = (n: number) => formatPercentValue((n / (totalOwnerImpacts || 1)) * 100);

    const sortedOwners = [...ownersData].sort((a, b) => b.impacts - a.impacts);

    const top = sortedOwners.slice(0, TOP_N);
    const othersImpacts = Math.max(0, totalOwnerImpacts - top.reduce((s, d) => s + d.impacts, 0));

    const ownerInsightsTop = top.map(d => ({
        label: (
            <>
                <strong style={{ color: brand }}>{pct(d.impacts)}</strong>&nbsp;of {metricWord}{' '}
                delivered on {d.label}
            </>
        ),
        value: '',
    }));

    const ownerInsights =
        sortedOwners.length > TOP_N && othersImpacts > 0
            ? [
                  ...ownerInsightsTop,
                  {
                      label: (
                          <>
                              <strong style={{ color: brand }}>{pct(othersImpacts)}</strong>&nbsp;of{' '}
                              {metricWord} delivered on <em>Others</em>
                          </>
                      ),
                      value: '',
                  },
              ]
            : ownerInsightsTop;

    const fmtMillionsAxis = (n: number) => `${(n / 1_000_000).toFixed(1)}M`;
    const pctOf = (part: number, total: number) =>
        total > 0 ? formatPercentValue((part / total) * 100) : '0%';

    // 5) normalise & prepare data for chart + insights
    const formatsData = useMemo(() => {
        const raw = (format as any)?.data?.data ?? (format as any)?.data ?? {};
        let rows: Array<{ label: string; impacts: number }> = [];

        if (Array.isArray(raw)) {
            rows = raw.map((r: any) => ({
                label: String(r.key ?? r.label ?? ''),
                impacts: Number(r.impacts ?? r?.value?.impacts ?? 0) || 0,
            }));
        } else {
            rows = Object.entries(raw).map(([k, v]: any) => ({
                label: String(k),
                impacts: Number(v?.impacts ?? v?.[k]?.impacts ?? 0) || 0,
            }));
        }

        return rows.sort((a, b) => b.impacts - a.impacts).splice(0, 10);
    }, [format]);

    const formatInsights = useMemo(() => {
        const INSIGHTS_MAX = 6;
        const sorted = [...formatsData].sort((a, b) => b.impacts - a.impacts);
        const total = sorted.reduce((s, r) => s + r.impacts, 0);
        const top = sorted.slice(0, INSIGHTS_MAX);
        const topSum = top.reduce((s, r) => s + r.impacts, 0);
        const others = Math.max(0, total - topSum);

        const rows = top.map(d => ({
            label: (
                <>
                    <strong style={{ color: brand }}>{pctOf(d.impacts, total)}</strong>&nbsp;of{' '}
                    {metricWord} delivered on {d.label}
                </>
            ),
            value: '',
        }));

        if (others > 0) {
            rows.push({
                label: (
                    <>
                        <strong style={{ color: brand }}>{pctOf(others, total)}</strong>&nbsp;of{' '}
                        {metricWord} delivered on Others
                    </>
                ),
                value: '',
            });
        }
        return rows;
    }, [formatsData, brand, metricWord]);

    const geoWord = payload.language === 'GB' ? 'Region' : 'State';

    const regionData = React.useMemo(() => {
        const src =
            (pcaSummary as any)?.data?.data?.display_tv_region ??
            (pcaSummary as any)?.data?.display_tv_region ??
            {};

        let rows: Array<{ label: string; impacts: number }> = [];

        if (Array.isArray(src)) {
            rows = src.map((r: any) => ({
                label: String(r.key ?? r.label ?? ''),
                impacts: Number(r.impacts ?? r?.value?.impacts ?? 0) || 0,
            }));
        } else if (src && typeof src === 'object') {
            rows = Object.entries(src)
                .filter(([k]) => k !== '')
                .map(([k, v]: any) => ({
                    label: String(k),
                    impacts: Number(v?.impacts ?? v?.[k]?.impacts ?? v ?? 0) || 0,
                }));
        }
        return rows.sort((a, b) => b.impacts - a.impacts).splice(0, 10);
    }, [pcaSummary]);

    const regionInsights = React.useMemo(() => {
        const INSIGHTS_MAX = 6;
        const sorted = [...regionData].sort((a, b) => b.impacts - a.impacts);
        const total = sorted.reduce((s, r) => s + r.impacts, 0);
        const top = sorted.slice(0, INSIGHTS_MAX);
        const topSum = top.reduce((s, r) => s + r.impacts, 0);
        const others = Math.max(0, total - topSum);

        const rows = top.map(d => ({
            label: (
                <>
                    <strong style={{ color: brand }}>{pctOf(d.impacts, total)}</strong>&nbsp;of{' '}
                    {metricWord} delivered in {d.label}
                </>
            ),
            value: '',
        }));

        if (others > 0) {
            rows.push({
                label: (
                    <>
                        <strong style={{ color: brand }}>{pctOf(others, total)}</strong>&nbsp;of{' '}
                        {metricWord} delivered on Others
                    </>
                ),
                value: '',
            });
        }
        return rows;
    }, [regionData, brand, metricWord]);

    const dayOfWeekData = useMemo(() => {
        const raw = (dow as any)?.data?.data ?? (dow as any)?.data ?? {};
        let rows: Array<{ label: string; impacts: number }> = [];

        if (Array.isArray(raw)) {
            rows = raw.map((r: any) => ({
                label: dowLabelFromKey(r.key ?? r.label ?? ''),
                impacts: Number(r.impacts ?? r?.value?.impacts ?? 0) || 0,
            }));
        } else {
            rows = Object.entries(raw).map(([k, v]: any) => ({
                label: dowLabelFromKey(k),
                impacts: Number(v?.impacts ?? v?.[k]?.impacts ?? v ?? 0) || 0,
            }));
        }

        return rows.sort((a, b) => dowIndex(a.label) - dowIndex(b.label));
    }, [dow]);

    const dayOfWeekInsights = useMemo(() => {
        const total = dayOfWeekData.reduce((s, r) => s + r.impacts, 0) || 1;
        return dayOfWeekData.map(d => ({
            label: (
                <>
                    <strong style={{ color: brand }}>{`${Math.round(
                        (d.impacts / total) * 100,
                    )}%`}</strong>
                    &nbsp;of {metricWord} delivered on {d.label}
                </>
            ),
            value: '',
        }));
    }, [dayOfWeekData, brand, metricWord]);
    // --- Hour of Day ---
    const hourOfDayData = useMemo(() => {
        const raw = (hour as any)?.data?.data ?? (hour as any)?.data ?? {};
        let rows: Array<{ hour: number; impacts: number }> = [];

        if (Array.isArray(raw)) {
            rows = raw
                .map((r: any) => ({
                    hour: Number(r.key ?? r.label ?? r.hour ?? 0),
                    impacts: Number(r.impacts ?? r?.value?.impacts ?? 0) || 0,
                }))
                .filter(r => Number.isFinite(r.hour));
        } else {
            rows = Object.entries(raw)
                .map(([k, v]: any) => ({
                    hour: Number(k),
                    impacts: Number(v?.impacts ?? v?.[k]?.impacts ?? v ?? 0) || 0,
                }))
                .filter(r => Number.isFinite(r.hour));
        }
        return rows.sort((a, b) => a.hour - b.hour);
    }, [hour]);

    const hourOfDayInsights = useMemo(() => {
        const total = hourOfDayData.reduce((s, r) => s + r.impacts, 0) || 1;
        const daySum = hourOfDayData
            .filter(r => r.hour >= 5 && r.hour <= 22)
            .reduce((s, r) => s + r.impacts, 0);
        const nightSum = Math.max(0, total - daySum);

        const use12hSkin = payload.skin_id === 38;
        const dayRange = use12hSkin ? '5 AM – 11 PM' : '05:00 – 23:00';
        const nightRange = use12hSkin ? '11 PM – 5 AM' : '23:00 – 05:00';

        const pct = (n: number) => formatPercentValue((n / total) * 100);
        return [
            {
                label: (
                    <>
                        <strong style={{ color: brand }}>{pct(daySum)}</strong>&nbsp;of {metricWord}{' '}
                        delivered between {dayRange}
                    </>
                ),
                value: '',
            },
            {
                label: (
                    <>
                        <strong style={{ color: brand }}>{pct(nightSum)}</strong>&nbsp;of{' '}
                        {metricWord} delivered between {nightRange}
                    </>
                ),
                value: '',
            },
        ];
    }, [hourOfDayData, brand, metricWord, payload.skin_id]);

    // Normalized arrays
    const geoRows = useMemo(() => normalizeRows(geo), [geo]);
    const nonGeoRows = useMemo(() => normalizeRows(imp), [imp]);

    const geoTop = useMemo(() => pickTopBottom(geoRows, true), [geoRows]);
    const geoBottom = useMemo(() => pickTopBottom(geoRows, false), [geoRows]);
    const nonGeoTop = useMemo(() => pickTopBottom(nonGeoRows, true), [nonGeoRows]);
    const nonGeoBottom = useMemo(() => pickTopBottom(nonGeoRows, false), [nonGeoRows]);

    const dspLabel = payload.language === 'GB' ? 'DSP Impacts' : 'DSP Impressions';
    const geoImpLabel = useMemo(() => {
        if (payload.language === 'GB') {
            if (routeTranslation) {
                return 'Route Impacts';
            }
            return 'Geopath Impacts';
        }
        if (routeTranslation) {
            return 'Route Impressions';
        }
        return 'Geopath Impressions';
    }, [payload.language, routeTranslation]);

    // payload.language === 'GB' ? 'Geopath Impacts' : 'Geopath Impressions';

    const GlossaryTerm: React.FC<{ term: string; children: React.ReactNode }> = ({
        term,
        children,
    }) => (
        <Box sx={{ mb: 3.5 }}>
            <Typography sx={{ fontWeight: 900, color: brand, mb: 0.5 }}>{term}</Typography>
            <Typography sx={{ lineHeight: 1.45 }}>{children}</Typography>
        </Box>
    );

    const reportTarget = dspReportTarget((campaign as any)?.data?.data, Number(impacts) || 0);
    if (!isReady) {
        return (
            <Box sx={{ minHeight: 320, display: 'grid', placeItems: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <style>{`
/* Print setup */
@page { size: A4 landscape; margin: 0; }
:root{
  --page-w: 297mm;
  --page-h: 210mm;
  --header-h: 40px;
  --footer-h: 64px;
  --pad-x: 24px;
  --pad-top-extra: 24px;
  --pad-bot-extra: 16px;
  --split-w: 50%;         /* EXACT 50/50 split for backgrounds */
  --footer-bar-h: 18px;   /* height of the pink footer bar */
}

@media screen{
  .page{
    width: 1200px;
    min-height: 700px;
    margin: 16px auto;
    position: relative;
    box-sizing: border-box;
    background: #ffffff; /* default page is WHITE */
    padding: calc(var(--header-h) + var(--pad-top-extra)) var(--pad-x) calc(var(--footer-h) + var(--pad-bot-extra));
    font-family: Inter, system-ui, sans-serif;
  }
  .page--grey {
  background: #f3f4f6 !important;
}
  .page + .page{ margin-top: 24px; }

  .page .page-header{
    position: absolute; top: 0; left: 0; right: 0; height: var(--header-h);
    padding: 12px calc(var(--pad-x) + 8px) 0 var(--pad-x);
    overflow: hidden;
  }
  .page .page-footer{
    position: absolute; left: 0; right: 0; bottom: 0; height: var(--footer-h);
    padding: 0 var(--pad-x);
  }

  .header-logo{
    display: block;
    max-height: 44px;
    max-width: 240px;
    object-fit: contain;
    margin-top: 4px;
  }

  /* Split backgrounds (50/50) */
  .split-bg-left{
    background: linear-gradient(to right, #f3f4f6 0 var(--split-w), #ffffff var(--split-w) 100%);
  }
  .split-bg-right{
    background: linear-gradient(to right, #ffffff 0 var(--split-w), #f3f4f6 var(--split-w) 100%);
  }
  .split-divider{
    position: absolute;
    top: calc(var(--header-h) + 8px);
    bottom: 0;
    left: var(--split-w);
    width: 1px;
    background: rgba(0,0,0,0.08);
  }

  /* Optional grid helper if needed */
  .split-grid{
    display: grid;
    grid-template-columns: var(--split-w) 1fr;
    column-gap: 28px;
    align-items: start;
  }

  .footer-bar{
    position: absolute; left: 0; right: 0; bottom: 0; height: var(--footer-bar-h);
  }
}

@media print{
  html, body{ margin:0 !important; padding:0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }

.thankyou-center{
  display: flex;
  flex-direction: column;
  justify-content: center;   /* vertical centering */
  align-items: flex-start;   /* keep content left-aligned */
  min-height: calc(100vh - var(--header-h) - var(--footer-h) - var(--pad-top-extra) - var(--pad-bot-extra));
}


  .page{
    width: var(--page-w) !important;
    height: var(--page-h) !important;
    page-break-after: always;
    position: relative;
    box-sizing: border-box;
    padding: calc(var(--header-h) + 8px) var(--pad-x) calc(var(--footer-h));
    overflow: hidden;
    background: #ffffff !important; /* default is WHITE in print */
  }
  .page--grey {
    background: #f3f4f6 !important;
  }
  .page:last-child{ page-break-after: auto; }

  .page .page-header{
    position: absolute; top: 0; left: 0; right: 0; height: var(--header-h);
    padding: 12px calc(var(--pad-x) + 8px) 0 var(--pad-x);
    overflow: hidden;
  }
  .page .page-footer{
    position: absolute; bottom: 0; left: 0; right: 0; height: var(--footer-h);
    padding: 0 var(--pad-x);
    background: transparent;
  }
  .glossary-grid{
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 48px !important;              /* matches spacing={6} */
    align-items: start !important;
    margin: 0 !important;
    width: auto !important;
  }
  .glossary-grid.MuiGrid-container{ margin-left: 0 !important; }
  .glossary-grid > .MuiGrid-item{
    width: auto !important;
    max-width: none !important;
    flex-basis: auto !important;
    padding: 0 !important;             /* clear MUI spacing padding */
    margin: 0 !important;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .glossary-right{ padding-left: 0 !important; } /* rely on the gap instead */
}

  .header-logo{ max-height: 36px; max-width: 200px; margin-top: 4px; }

  .print-three > .MuiGrid-item{
    width: 33.3333% !important;
    flex-basis: 33.3333% !important;
  }
  .print-three .metric-card{ break-inside: avoid; }

  /* Force exact 50/50 split paints in print */
  .split-bg-left{
    background: linear-gradient(to right, #f3f4f6 0 50%, #ffffff 50% 100%) !important;
  }
  .split-bg-right{
    background: linear-gradient(to right, #ffffff 0 50%, #f3f4f6 50% 100%) !important;
  }
  .split-divider{
    position: absolute;
    top: calc(var(--header-h) + 8px);
    bottom: 0;
    left: 50%;
    width: 1px;
    background: rgba(0,0,0,0.08);
  }
  
}

/* Big cover title + underline */
.cover-title{
  font-size: 72px;
  font-weight: 900;
}
.cover-underline{
  height: 12px;
  border-radius: 2px;
  margin-top: 16px;
  width: min(680px, 70%);
}
`}</style>

            {/* SUMMARY (white) */}
            <Box className="page page--grey">
                <Box className="page-header">
                    <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="flex-end"
                        sx={{ width: 1 }}
                    >
                        <Box component="img" src={logoUrl} alt="Logo" className="header-logo" />
                    </Stack>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 900, color: brand, mb: 0.5 }}>
                    {title}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: brand, mb: 3 }}>
                    Campaign Summary
                </Typography>
                <Grid container spacing={2} sx={{ mb: 8, maxWidth: 820 }}>
                    <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 800, color: 'grey.700' }}>
                            {payload.language === 'GB'
                                ? 'Campaign Live Dates:'
                                : 'Campaign Live Dates:'}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 700 }}>{liveDates || '—'}</Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 800, color: 'grey.700' }}>
                            {payload.language === 'GB'
                                ? 'Report Data Range:'
                                : 'Report Data Range:'}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 700 }}>{reportDates || '—'}</Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 800, color: 'grey.700' }}>
                            {payload.language === 'GB' ? 'Campaign Number:' : 'Campaign Number:'}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography sx={{ fontWeight: 800 }}>#{payload.campaign_id}</Typography>
                    </Grid>
                </Grid>

                <Typography variant="h4" sx={{ fontWeight: 900, color: brand, mb: 2, mt: 14 }}>
                    {payload.language === 'GB' ? 'Performance Headlines' : 'Performance Headlines'}
                </Typography>

                <Grid container spacing={3} className="print-three" sx={{ maxWidth: 1240 }}>
                    <Grid item xs={12} md={4}>
                        <Box
                            className="metric-card"
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: '#fff',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    color: brand,
                                    mb: 1,
                                    borderBottom: `3px solid ${brand}`,
                                    display: 'inline-block',
                                }}
                            >
                                {payload.language === 'GB' ? 'Impacts' : 'Impressions'}
                            </Typography>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="baseline"
                            >
                                <Typography sx={{ color: 'text.secondary' }}>Total</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                                    {fmtN(impacts)}
                                </Typography>
                            </Stack>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box
                            className="metric-card"
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: '#fff',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    color: brand,
                                    mb: 1,
                                    borderBottom: `3px solid ${brand}`,
                                    display: 'inline-block',
                                }}
                            >
                                Plays
                            </Typography>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="baseline"
                            >
                                <Typography sx={{ color: 'text.secondary' }}>Total</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                                    {fmtN(plays)}
                                </Typography>
                            </Stack>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box
                            className="metric-card"
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: '#fff',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    color: brand,
                                    mb: 1,
                                    borderBottom: `3px solid ${brand}`,
                                    display: 'inline-block',
                                }}
                            >
                                Time Played
                            </Typography>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="baseline"
                            >
                                <Typography sx={{ color: 'text.secondary' }}>Total</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                                    {fmtTime(seconds)}
                                </Typography>
                            </Stack>
                        </Box>
                    </Grid>
                </Grid>
                <Box className="page-footer" sx={{ px: 2 }}>
                    <Stack direction="row" alignItems="flex-end" gap={2} sx={{ width: 1, pl: 1.5 }}>
                        <Box
                            component="img"
                            src={poweredByUniledLogo}
                            alt="Powered by UniLED"
                            sx={{ maxHeight: 36, objectFit: 'contain', display: 'block' }}
                        />
                        <Box
                            sx={{ flexGrow: 1, borderBottom: '1px solid', borderColor: 'black' }}
                        />
                    </Stack>
                </Box>
            </Box>

            {/* COVER: Delivery Data – grey LEFT / white RIGHT (50/50) */}
            <Box className="page split-bg-left">
                <Box className="page-header">
                    <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="flex-end"
                        sx={{ width: 1 }}
                    >
                        <Box component="img" src={logoUrl} alt="Logo" className="header-logo" />
                    </Stack>
                </Box>

                <Box sx={{ maxWidth: 'calc(var(--split-w))' }}>
                    <Box sx={{ mt: 2, pl: 1 }}>
                        <Typography
                            sx={{ color: 'black', 'font-size': '72px', 'font-weight': '900' }}
                        >
                            Delivery Data
                            <br />& Insights
                        </Typography>
                        <Box className="cover-underline" sx={{ bgcolor: brand }} />
                    </Box>
                </Box>
                <Box
                    className="page-footer"
                    sx={{ px: 2, position: 'absolute', left: 0, right: 0, bottom: 0 }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ height: 'calc(var(--footer-h) - 18px)', px: 'var(--pad-x)' }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            #{payload.campaign_id} | {title}
                        </Typography>
                        <Stack direction="row" alignItems="flex-end" gap={2}>
                            <Box
                                component="img"
                                src={poweredByUniledLogo}
                                alt="Powered by UniLED"
                                sx={{ maxHeight: 32, objectFit: 'contain', display: 'block' }}
                            />
                        </Stack>
                    </Stack>
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 18,
                            bgcolor: brand,
                        }}
                    />
                </Box>
            </Box>

            {/* CHART PAGES */}
            <DspSplitReportPage
                splitWidth="50%"
                logoUrl={logoUrl}
                brand={brand}
                campaignId={payload.campaign_id}
                campaignTitle={title}
            >
                <SplitChartWithInsights
                    language={payload.language}
                    brand={brand}
                    heading={
                        payload.language === 'GB'
                            ? 'Performance By Impacts'
                            : 'Performance By Impressions'
                    }
                    metricWord={payload.language === 'GB' ? 'Impacts' : 'Impressions'}
                    chart={
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={lineData}
                                margin={{ top: 8, right: 16, left: 0, bottom: 36 }}
                            >
                                <CartesianGrid stroke="#e5e7eb" vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 11 }}
                                    angle={-35}
                                    textAnchor="end"
                                    height={56}
                                    interval="preserveStartEnd"
                                />
                                <YAxis
                                    width={52}
                                    tick={{ fontSize: 12 }}
                                    tickFormatter={n => `${(n / 1_000_000).toFixed(1)}M`}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke={brand}
                                    strokeWidth={4}
                                    dot={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    }
                    insights={[
                        {
                            label:
                                payload.language === 'GB'
                                    ? 'Total Target Campaign Impacts:'
                                    : 'Total Target Campaign Impressions:',
                            value:
                                reportTarget.expected == null ? '—' : fmtN(reportTarget.expected),
                        },
                        {
                            label:
                                payload.language === 'GB'
                                    ? 'Total Impacts Received to Date:'
                                    : 'Total Impressions Received to Date:',
                            value: fmtN(impacts),
                        },
                        {
                            label:
                                payload.language === 'GB'
                                    ? 'Total of Target Delivered to Date:'
                                    : 'Total of Target Delivered to Date:',
                            value:
                                reportTarget.percent == null
                                    ? '—'
                                    : `${reportTarget.percent.toFixed(0)}%`,
                        },
                    ]}
                />
            </DspSplitReportPage>

            <DspSplitReportPage
                splitWidth="50%"
                logoUrl={logoUrl}
                brand={brand}
                campaignId={payload.campaign_id}
                campaignTitle={title}
            >
                <SplitChartWithInsights
                    language={payload.language}
                    brand={brand}
                    heading={`Performance By ${
                        payload.language === 'GB' ? 'Media Owner' : 'Partner'
                    }`}
                    metricWord={metricWord}
                    leftPillText={`${metricWord} by ${
                        payload.language === 'GB' ? 'Media Owner' : 'Partner'
                    }`}
                    rightPillText={`${
                        payload.language === 'GB' ? 'Media Owner' : 'Partner'
                    } Insights`}
                    chart={
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={ownersData}
                                margin={{ top: 8, right: 16, left: 0, bottom: 64 }}
                            >
                                <CartesianGrid stroke="#e5e7eb" vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 10 }}
                                    interval={0}
                                    angle={-50}
                                    textAnchor="end"
                                    height={70}
                                />
                                <YAxis
                                    width={48}
                                    tick={{ fontSize: 12 }}
                                    tickFormatter={(n: number) => `${(n / 1_000_000).toFixed(1)}M`}
                                />
                                <Bar dataKey="impacts" fill={brand} />
                            </BarChart>
                        </ResponsiveContainer>
                    }
                    insights={ownerInsights}
                    chartHeight={380}
                />
            </DspSplitReportPage>

            <DspSplitReportPage
                splitWidth="50%"
                logoUrl={logoUrl}
                brand={brand}
                campaignId={payload.campaign_id}
                campaignTitle={title}
            >
                <SplitChartWithInsights
                    language={payload.language}
                    brand={brand}
                    heading="Performance By Format"
                    metricWord={metricWord}
                    leftPillText={`${metricWord} By Format`}
                    rightPillText="Format Insights"
                    chart={
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={formatsData}
                                margin={{ top: 8, right: 16, left: 0, bottom: 64 }}
                            >
                                <CartesianGrid stroke="#e5e7eb" vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 10 }}
                                    interval={0}
                                    angle={-50}
                                    textAnchor="end"
                                    height={70}
                                />
                                <YAxis
                                    width={48}
                                    tick={{ fontSize: 12 }}
                                    tickFormatter={fmtMillionsAxis}
                                />
                                <Bar dataKey="impacts" fill={brand} />
                            </BarChart>
                        </ResponsiveContainer>
                    }
                    insights={formatInsights}
                    chartHeight={380}
                />
            </DspSplitReportPage>

            <DspSplitReportPage
                splitWidth="50%"
                logoUrl={logoUrl}
                brand={brand}
                campaignId={payload.campaign_id}
                campaignTitle={title}
            >
                <SplitChartWithInsights
                    language={payload.language}
                    brand={brand}
                    heading={`Performance By ${geoWord}`}
                    metricWord={metricWord}
                    leftPillText={`${metricWord} By ${geoWord}`}
                    rightPillText={`${geoWord} Insights`}
                    chart={
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={regionData}
                                margin={{ top: 8, right: 16, left: 0, bottom: 48 }}
                            >
                                <CartesianGrid stroke="#e5e7eb" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 12 }} height={28} />
                                <YAxis
                                    width={56}
                                    tick={{ fontSize: 12 }}
                                    tickFormatter={fmtMillionsAxis}
                                />
                                <Bar dataKey="impacts" fill={brand} />
                            </BarChart>
                        </ResponsiveContainer>
                    }
                    insights={regionInsights}
                    chartHeight={360}
                />
            </DspSplitReportPage>

            <DspSplitReportPage
                splitWidth="50%"
                logoUrl={logoUrl}
                brand={brand}
                campaignId={payload.campaign_id}
                campaignTitle={title}
            >
                <SplitChartWithInsights
                    language={payload.language}
                    brand={brand}
                    heading="Performance By Day of Week"
                    metricWord={metricWord}
                    leftPillText={`${metricWord} By Day of Week`}
                    rightPillText="Day of Week Insights"
                    chart={
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={dayOfWeekData}
                                margin={{ top: 8, right: 16, left: 0, bottom: 48 }}
                            >
                                <CartesianGrid stroke="#e5e7eb" vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 12 }}
                                    height={28}
                                    interval={0}
                                    angle={-50}
                                    textAnchor="end"
                                />
                                <YAxis
                                    width={56}
                                    tick={{ fontSize: 12 }}
                                    tickFormatter={(n: number) => `${(n / 1_000_000).toFixed(1)}M`}
                                />
                                <Bar dataKey="impacts" fill={brand} />
                            </BarChart>
                        </ResponsiveContainer>
                    }
                    insights={dayOfWeekInsights}
                    chartHeight={360}
                />
            </DspSplitReportPage>

            <DspSplitReportPage
                splitWidth="50%"
                logoUrl={logoUrl}
                brand={brand}
                campaignId={payload.campaign_id}
                campaignTitle={title}
            >
                <SplitChartWithInsights
                    language={payload.language}
                    brand={brand}
                    heading="Performance By Hour of Day"
                    metricWord={metricWord}
                    leftPillText={`${metricWord} By Hour of Day`}
                    rightPillText="Hour of Day Insights"
                    chart={
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={hourOfDayData}
                                margin={{ top: 8, right: 16, left: 0, bottom: 24 }}
                            >
                                <CartesianGrid stroke="#e5e7eb" vertical={false} />
                                <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                                <YAxis
                                    width={56}
                                    tick={{ fontSize: 12 }}
                                    tickFormatter={(n: number) => `${(n / 1_000_000).toFixed(1)}M`}
                                />
                                <Bar dataKey="impacts" fill={brand} />
                            </BarChart>
                        </ResponsiveContainer>
                    }
                    insights={hourOfDayInsights}
                    chartHeight={360}
                />
            </DspSplitReportPage>

            {/* COVER: Impact/Impression Data – grey LEFT / white RIGHT (50/50) */}
            <Box className="page split-bg-left">
                <Box className="page-header">
                    <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="flex-end"
                        sx={{ width: 1 }}
                    >
                        <Box component="img" src={logoUrl} alt="Logo" className="header-logo" />
                    </Stack>
                </Box>

                <Box sx={{ maxWidth: 'calc(var(--split-w))' }}>
                    <Box sx={{ mt: 2, pl: 1 }}>
                        <Typography
                            sx={{ color: 'black', 'font-size': '72px', 'font-weight': '900' }}
                        >
                            {payload.language === 'GB' ? 'Impact' : 'Impression'} Data
                            <br />& Insights
                        </Typography>
                        <Box className="cover-underline" sx={{ bgcolor: brand }} />
                    </Box>
                </Box>
                <Box
                    className="page-footer"
                    sx={{ px: 2, position: 'absolute', left: 0, right: 0, bottom: 0 }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ height: 'calc(var(--footer-h) - 18px)', px: 'var(--pad-x)' }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            #{payload.campaign_id} | {title}
                        </Typography>
                        <Stack direction="row" alignItems="flex-end" gap={2}>
                            <Box
                                component="img"
                                src={poweredByUniledLogo}
                                alt="Powered by UniLED"
                                sx={{ maxHeight: 32, objectFit: 'contain', display: 'block' }}
                            />
                        </Stack>
                    </Stack>
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 18,
                            bgcolor: brand,
                        }}
                    />
                </Box>
            </Box>

            {/* ---------------- TOP (Greater value) ---------------- */}
            <Box className="page" sx={{ bgcolor: '#fff' }}>
                <Box className="page-header">
                    <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="flex-end"
                        sx={{ width: 1 }}
                    >
                        <Box component="img" src={logoUrl} alt="Logo" className="header-logo" />
                    </Stack>
                </Box>

                <Typography variant="h3" sx={{ fontWeight: 900, mb: 2 }}>
                    {payload.language === 'GB' ? 'Impact' : 'Impression'} | Optimizations to
                    Maintain & Grow
                </Typography>
                <Typography sx={{ fontWeight: 800, mb: 1.5, fontSize: 20 }}>
                    Top 5 ({payload.language === 'GB' ? 'Impact Delivery' : 'Impression Delivery'})
                </Typography>

                <Typography sx={{ mb: 1.5 }}>
                    <strong>{routeTranslation ? 'Route' : 'Geopath'} Audited Inventory:</strong> The
                    Average {routeTranslation ? 'Route' : 'Geopath'} DSP IM is{' '}
                    <strong style={{ color: brand }}>HIGHER</strong> than the DSP IM for the same
                    format type *
                </Typography>
                <ImpactTable
                    rows={geoTop}
                    variant="geo"
                    positive
                    brand={brand}
                    dspLabel={dspLabel}
                    geoImpLabel={geoImpLabel}
                    isRoute={routeTranslation}
                />

                <Typography sx={{ mt: 4, mb: 1.5 }}>
                    <strong>Non-{routeTranslation ? 'Route' : 'Geopath'} Audited Inventory:</strong>{' '}
                    The Average Format DSP IM is <strong style={{ color: brand }}>HIGHER</strong>{' '}
                    than the DSP IM for the same format type
                </Typography>
                <ImpactTable
                    rows={nonGeoTop}
                    variant="nongeo"
                    positive
                    brand={brand}
                    dspLabel={dspLabel}
                    geoImpLabel={geoImpLabel}
                    isRoute={routeTranslation}
                />

                <Box
                    className="page-footer"
                    sx={{ px: 2, position: 'absolute', left: 0, right: 0, bottom: 0 }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ height: 'calc(var(--footer-h) - 18px)', px: 'var(--pad-x)' }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            #{payload.campaign_id} | {title}
                        </Typography>
                        <Stack direction="row" alignItems="flex-end" gap={2}>
                            <Box
                                component="img"
                                src={poweredByUniledLogo}
                                alt="Powered by UniLED"
                                sx={{ maxHeight: 32, objectFit: 'contain', display: 'block' }}
                            />
                        </Stack>
                    </Stack>
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 18,
                            bgcolor: brand,
                        }}
                    />
                </Box>
            </Box>

            {/* ---------------- BOTTOM (Less value) ---------------- */}
            <Box className="page" sx={{ bgcolor: '#fff' }}>
                <Box className="page-header">
                    <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="flex-end"
                        sx={{ width: 1 }}
                    >
                        <Box component="img" src={logoUrl} alt="Logo" className="header-logo" />
                    </Stack>
                </Box>

                <Typography variant="h3" sx={{ fontWeight: 900, mb: 2 }}>
                    {payload.language === 'GB' ? 'Impact' : 'Impression'} Analysis | Optimizations
                    to Review & Consider
                </Typography>
                <Typography sx={{ fontWeight: 800, mb: 1.5, fontSize: 20 }}>
                    Top 5 ({payload.language === 'GB' ? 'Impact Delivery' : 'Impression Delivery'})
                </Typography>

                <Typography sx={{ mb: 1.5 }}>
                    <strong>{routeTranslation ? 'Route' : 'Geopath'} Audited Inventory:</strong> The
                    Average {routeTranslation ? 'Route' : 'Geopath'} DSP IM is{' '}
                    <strong style={{ color: '#d32f2f' }}>LOWER</strong> than the DSP IM for the same
                    format type *
                </Typography>
                <ImpactTable
                    rows={geoBottom}
                    variant="geo"
                    positive={false}
                    brand={brand}
                    dspLabel={dspLabel}
                    geoImpLabel={geoImpLabel}
                    isRoute={routeTranslation}
                />

                <Typography sx={{ mt: 4, mb: 1.5 }}>
                    <strong>Non-{routeTranslation ? 'Route' : 'Geopath'} Audited Inventory:</strong>{' '}
                    The Average Format DSP IM is <strong style={{ color: '#d32f2f' }}>LOWER</strong>{' '}
                    than the DSP IM for the same format type
                </Typography>
                <ImpactTable
                    rows={nonGeoBottom}
                    variant="nongeo"
                    positive={false}
                    brand={brand}
                    dspLabel={dspLabel}
                    geoImpLabel={geoImpLabel}
                    isRoute={routeTranslation}
                />

                <Box
                    className="page-footer"
                    sx={{ px: 2, position: 'absolute', left: 0, right: 0, bottom: 0 }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ height: 'calc(var(--footer-h) - 18px)', px: 'var(--pad-x)' }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            #{payload.campaign_id} | {title}
                        </Typography>
                        <Stack direction="row" alignItems="flex-end" gap={2}>
                            <Box
                                component="img"
                                src={poweredByUniledLogo}
                                alt="Powered by UniLED"
                                sx={{ maxHeight: 32, objectFit: 'contain', display: 'block' }}
                            />
                        </Stack>
                    </Stack>
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 18,
                            bgcolor: brand,
                        }}
                    />
                </Box>
            </Box>

            {/* COVER: Glossary of Terms – grey LEFT / white RIGHT */}
            <Box className="page split-bg-left">
                <Box className="page-header">
                    <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="flex-end"
                        sx={{ width: 1 }}
                    >
                        <Box component="img" src={logoUrl} alt="Logo" className="header-logo" />
                    </Stack>
                </Box>

                <Box sx={{ maxWidth: 'calc(var(--split-w))' }}>
                    <Box sx={{ mt: 2, pl: 1 }}>
                        <Typography
                            sx={{ color: 'black', 'font-size': '72px', 'font-weight': '900' }}
                        >
                            Glossary of
                            <br />
                            Terms
                        </Typography>
                        <Box className="cover-underline" sx={{ bgcolor: brand }} />
                    </Box>
                </Box>
                <Box
                    className="page-footer"
                    sx={{ px: 2, position: 'absolute', left: 0, right: 0, bottom: 0 }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ height: 'calc(var(--footer-h) - 18px)', px: 'var(--pad-x)' }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            #{payload.campaign_id} | {title}
                        </Typography>
                        <Stack direction="row" alignItems="flex-end" gap={2}>
                            <Box
                                component="img"
                                src={poweredByUniledLogo}
                                alt="Powered by UniLED"
                                sx={{ maxHeight: 32, objectFit: 'contain', display: 'block' }}
                            />
                        </Stack>
                    </Stack>
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 18,
                            bgcolor: brand,
                        }}
                    />
                </Box>
            </Box>

            {/* GLOSSARY CONTENT: white LEFT / grey RIGHT (50/50) */}
            <Box className="page split-bg-right">
                <Box className="page-header">
                    <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="flex-end"
                        sx={{ width: 1 }}
                    >
                        <Box component="img" src={logoUrl} alt="Logo" className="header-logo" />
                    </Stack>
                </Box>

                <Typography variant="h3" sx={{ fontWeight: 900, mb: 4 }}>
                    Glossary
                </Typography>

                {/* Force 2 columns in print via .glossary-grid (see CSS) */}
                <Grid container spacing={6} className="glossary-grid">
                    {/* Left column: terms (white half) */}
                    <Grid item xs={12} md={6}>
                        <GlossaryTerm term="DSP">
                            A Demand Side Platform is software that automates and streamlines the
                            purchase of DOOH media for media buyers
                        </GlossaryTerm>
                        <GlossaryTerm term="SSP">
                            A Supply Side Platform offers software that automates and streamlines
                            the selling of a media owner’s DOOH inventory
                        </GlossaryTerm>
                        <GlossaryTerm term="Play Data">
                            Every time an ad is played on screen, a play log is created and recorded
                            to include the start and end time, duration and creative asset name
                        </GlossaryTerm>
                        <GlossaryTerm term="DSP Play Data">
                            Play Data reported by the DSP
                        </GlossaryTerm>
                        <GlossaryTerm term="SSP Play Data">
                            Play Data reported by the SSP
                        </GlossaryTerm>
                        <GlossaryTerm term="Vendor Player Data">
                            Play Data captured via the CMS (Content Management System) on the screen
                            player
                        </GlossaryTerm>
                    </Grid>

                    {/* Right column: long-form copy (grey half) */}
                    <Grid
                        item
                        xs={12}
                        md={6}
                        className="glossary-right" // clears left padding in print; keep pl on screen
                        sx={{ pl: { md: 4 } }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 900, color: brand, mb: 1 }}>
                            {metricWordSignular} Multiplier (IM)
                        </Typography>
                        <Typography paragraph sx={{ lineHeight: 1.45 }}>
                            The {metricWordSignular} multiplier is a DOOH-specific multiplication
                            index applied to each play on every DOOH screen to estimate the number
                            of {metricWordSignular}s delivered in a single ad play, at any given
                            day/hour.
                        </Typography>
                        <Typography paragraph sx={{ lineHeight: 1.45 }}>
                            Unlike most other digital advertising channels which operate on a
                            one-to-one basis (meaning one play equals one {metricWordSignular}),
                            DOOH is a one-to-many medium, meaning one play not only equals multiple{' '}
                            {metricWordSignular}s but the number of {metricWordSignular}s usually
                            differs depending on the unit, day, hour, and many other factors in the
                            OOH space. Because each {metricWordSignular} can reach multiple viewers,
                            a multiplier is needed to more accurately estimate how many viewers saw
                            the ad.
                        </Typography>
                        <Typography paragraph sx={{ lineHeight: 1.45 }}>
                            In the advancing world of programmatic DOOH, the {metricWordSignular}{' '}
                            multiplier was introduced to account for the broader reach of any given
                            play on a DOOH screen and allows DOOH to align itself with the{' '}
                            {metricWordSignular}-based buying model while remaining true to the
                            greatest strength of the medium – its capacity for mass reach.
                        </Typography>

                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 900, color: brand, mt: 4, mb: 1 }}
                        >
                            {routeTranslation ? 'Route' : 'Geopath'} {metricWordSignular}
                        </Typography>
                        <Typography sx={{ lineHeight: 1.45 }}>
                            {routeTranslation ? 'Route' : 'Geopath'} generates the industry standard
                            audience measurements for out of home media.{' '}
                            {routeTranslation ? 'Route' : 'Geopath'} is OOH’s governing measurement
                            body to establish and supervise practices for the collection, recording,
                            authentication, and verification of data related to the measurement of
                            OOH audiences.
                        </Typography>
                    </Grid>
                </Grid>

                {/* Optional thin divider at the 50% split */}

                <Box
                    className="page-footer"
                    sx={{ px: 2, position: 'absolute', left: 0, right: 0, bottom: 0 }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ height: 'calc(var(--footer-h) - 18px)', px: 'var(--pad-x)' }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            #{payload.campaign_id} | {title}
                        </Typography>
                        <Stack direction="row" alignItems="flex-end" gap={2}>
                            <Box
                                component="img"
                                src={poweredByUniledLogo}
                                alt="Powered by UniLED"
                                sx={{ maxHeight: 32, objectFit: 'contain', display: 'block' }}
                            />
                        </Stack>
                    </Stack>
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 18,
                            bgcolor: brand,
                        }}
                    />
                </Box>
            </Box>

            {/* GLOSSARY: Split explainer – grey LEFT / white RIGHT */}
            <Box className="page split-bg-left">
                <Box className="page-header">
                    <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="flex-end"
                        sx={{ width: 1 }}
                    >
                        <Box component="img" src={logoUrl} alt="Logo" className="header-logo" />
                    </Stack>
                </Box>

                <Typography variant="h3" sx={{ fontWeight: 900, mb: 6, pl: 1 }}>
                    Glossary
                </Typography>

                <Box sx={{ maxWidth: 'calc(var(--split-w) - var(--pad-x))', pl: 1 }}>
                    <Typography sx={{ fontWeight: 900, color: brand, fontSize: 28, mb: 1.5 }}>
                        {routeTranslation ? 'Route' : 'Geopath'} Audited Analysis
                    </Typography>
                    <Typography sx={{ lineHeight: 1.45, mb: 6 }}>
                        An analysis of {routeTranslation ? 'Route' : 'Geopath'} Audited Units.{' '}
                        <br />
                        Comparing DSP {metricWordSignular}s vs{' '}
                        {routeTranslation ? 'Route' : 'Geopath'} {metricWordSignular}s for the
                        campaign.
                    </Typography>

                    <Typography sx={{ fontWeight: 900, color: brand, fontSize: 28, mb: 1.5 }}>
                        Non-{routeTranslation ? 'Route' : 'Geopath'} Audited Analysis
                    </Typography>
                    <Typography sx={{ lineHeight: 1.45 }}>
                        An analysis of Non-{routeTranslation ? 'Route' : 'Geopath'} Audited Units.{' '}
                        <br />
                        Comparing Format {metricWordSignular}s against the Average Format{' '}
                        {metricWordSignular}s for the campaign.
                    </Typography>
                </Box>

                <Box
                    className="page-footer"
                    sx={{ px: 2, position: 'absolute', left: 0, right: 0, bottom: 0 }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ height: 'calc(var(--footer-h) - 18px)', px: 'var(--pad-x)' }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            #{payload.campaign_id} | {title}
                        </Typography>
                        <Stack direction="row" alignItems="flex-end" gap={2}>
                            <Box
                                component="img"
                                src={poweredByUniledLogo}
                                alt="Powered by UniLED"
                                sx={{ maxHeight: 32, objectFit: 'contain', display: 'block' }}
                            />
                        </Stack>
                    </Stack>
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 18,
                            bgcolor: brand,
                        }}
                    />
                </Box>
            </Box>
            <Box className="page">
                <Box className="thankyou-center">
                    <Box>
                        <Typography sx={{ color: 'black', fontSize: '90px', fontWeight: 900 }}>
                            Thank you
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            bgcolor: brand,
                            height: '20px',
                            borderRadius: '2px',
                            width: 500,
                        }}
                    />

                    <Stack direction="row" alignItems="center" spacing={3} pt={2}>
                        <Box
                            component="img"
                            src={uniledLogo}
                            alt="UniLED"
                            className="final-logo"
                            sx={{ width: 150, objectFit: 'contain', display: 'block' }}
                        />
                        <Box
                            sx={{
                                height: 48,
                                borderLeft: '1px solid',
                                borderColor: 'rgb(124,124,124)',
                            }}
                        />
                        <Box
                            component="img"
                            src={logoUrl}
                            alt="Partner"
                            className="final-logo"
                            sx={{ width: 150, objectFit: 'contain', display: 'block' }}
                        />
                    </Stack>
                </Box>
            </Box>
        </>
    );
};

function ImpactTable({
    rows,
    variant, // 'geo' | 'nongeo'
    brand,
    positive, // true = green badge, false = orange
    dspLabel,
    geoImpLabel,
    isRoute,
}: {
    rows: MultRow[];
    variant: 'geo' | 'nongeo';
    brand: string;
    positive: boolean;
    dspLabel: string;
    geoImpLabel: string;
    isRoute: boolean;
}) {
    const headerSx = {
        bgcolor: brand,
        color: '#fff',
        fontWeight: 800,
        fontSize: 11,
        whiteSpace: 'nowrap' as const,
    };

    const badgeBg = positive ? '#AEDDAB' : '#F4B084';

    const baseCols = [
        { id: 'display_owner', label: 'Display Owner', align: 'left', fmt: (v: any) => v },
        { id: 'display_format', label: 'Display Format', align: 'left', fmt: (v: any) => v },
        { id: 'sum_of_play', label: '# Plays', align: 'right', fmt: fmtInt },
        { id: 'dsp_impressions', label: dspLabel, align: 'right', fmt: fmtInt },
    ] as const;

    const geoCols = [
        ...baseCols,
        { id: 'geopath_impressions', label: geoImpLabel, align: 'right', fmt: fmtInt },
        { id: 'variance', label: 'VAR', align: 'right', fmt: fmt2 },
        { id: 'dsp_avg_impressions', label: 'DSP IM', align: 'right', fmt: fmt2 },
        {
            id: 'geopath_avg_impressions',
            label: isRoute ? 'Route Im' : 'Geopath IM',
            align: 'right',
            fmt: fmt2,
        },
        { id: 'average_im', label: 'Average Format IM', align: 'right', fmt: fmt2 },
    ] as const;

    const nonGeoCols = [
        ...baseCols,
        { id: 'dsp_avg_impressions', label: 'DSP IM', align: 'right', fmt: fmt2 },
        { id: 'average_im', label: 'Average IM', align: 'right', fmt: fmt2 },
        { id: 'variance', label: 'IM VAR', align: 'right', fmt: fmt2 },
    ] as const;

    const cols = variant === 'geo' ? geoCols : nonGeoCols;

    return (
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        >
            <Table size="small" aria-label="impact table">
                <TableHead>
                    <TableRow>
                        {cols.map(c => (
                            <TableCell key={c.id as string} align="center" sx={headerSx}>
                                {c.label}
                            </TableCell>
                        ))}
                        <TableCell align="center" sx={headerSx}>
                            % VAR
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <TableRow key={idx}>
                            {cols.map(c => {
                                const raw = r[c.id] as any;
                                const val = c.fmt(raw);
                                return (
                                    <TableCell
                                        key={String(c.id)}
                                        align="center"
                                        sx={{ fontSize: 10, border: '1px solid black' }}
                                    >
                                        {val}
                                    </TableCell>
                                );
                            })}
                            <TableCell
                                align="center"
                                sx={{
                                    fontSize: 10,
                                    bgcolor: badgeBg,
                                    fontWeight: 700,
                                    border: '1px solid black',
                                }}
                            >
                                {fmtPct(r.variance_percent)}
                            </TableCell>
                        </TableRow>
                    ))}
                    {!rows.length && (
                        <TableRow>
                            <TableCell
                                colSpan={cols.length + 1}
                                sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                            >
                                No inventory applicable here.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default DSPReport;
